import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

// Lightweight in-house i18n — no dependency. The app ships in three
// languages; anything else falls back to English (product decision).
export type Lang = "pl" | "en" | "de";

export const LANGS: Lang[] = ["pl", "en", "de"];
export const LANG_LABEL: Record<Lang, string> = { pl: "PL", en: "EN", de: "DE" };

const STORAGE_KEY = "sytofood.lang";

// Maps a raw `navigator.language` to a supported Lang. Visitors whose
// browser is set to anything outside pl/de/en land on English.
export function detectBrowserLang(): Lang {
  if (typeof navigator === "undefined") return "en";
  const raw = (navigator.language || "en").toLowerCase();
  if (raw.startsWith("pl")) return "pl";
  if (raw.startsWith("de")) return "de";
  if (raw.startsWith("en")) return "en";
  return "en";
}

export function readStoredLang(): Lang | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "pl" || v === "en" || v === "de" ? v : null;
  } catch {
    return null;
  }
}

function writeStoredLang(lang: Lang): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // storage blocked — language just won't persist across reloads
  }
}

type I18nValue = { lang: Lang; setLang: (lang: Lang) => void };

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // Initial language: an explicit stored choice wins; otherwise detect from
  // the browser. A signed-in user's server-side preferredLanguage is
  // reconciled separately once that lands (Phase 2).
  const [lang, setLangState] = useState<Lang>(() => readStoredLang() ?? detectBrowserLang());

  const setLang = useCallback((next: Lang) => {
    writeStoredLang(next);
    setLangState(next);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang }}>{children}</I18nContext.Provider>;
}

export function useLang(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useLang must be used inside <I18nProvider>");
  return ctx;
}
